import React, { useContext } from "react";
import {
  Box,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_ADD_CATEGORY_SOP,
  PAGE_ROUTE_ADD_PRODUCT_SOP,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_SOPS,
  PAGE_ROUTE_USER_GUIDE,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import CategoryMermaidDiagram from "./CategoryMermaidDiagram";
import PublicInfoCallout from "./PublicInfoCallout";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import PublicInfoPageHeader from "./PublicInfoPageHeader";
import PublicMarketingLayout from "./PublicMarketingLayout";
import {
  CATEGORY_PRODUCT_FORM_ROWS,
  EXAMPLE_PRODUCT_PATH_MERMAID,
  PAINT_CATEGORY_STRUCTURE_MERMAID
} from "./constants/categoriesLearnMoreContent";
import {
  publicInfoBodySx,
  publicInfoLinkSx,
  publicInfoOrderedListSx,
  publicInfoSectionPanelSx,
  publicInfoSectionStackSx,
  publicInfoSectionTitleSx
} from "./constants/publicInfoPageStyles";

const CategoriesLearnMorePage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};

  const pathFor = (route: string) =>
    language != null && defaultLang != null
      ? localizedPath(route, language, defaultLang)
      : `/${route}`;

  const addCategorySopPath = pathFor(PAGE_ROUTE_ADD_CATEGORY_SOP);
  const addProductSopPath = pathFor(PAGE_ROUTE_ADD_PRODUCT_SOP);
  const sopsHubPath = pathFor(PAGE_ROUTE_SOPS);
  const userGuidePath = pathFor(PAGE_ROUTE_USER_GUIDE);
  const contactPath = pathFor(PAGE_ROUTE_CONTACT);

  return (
    <PublicMarketingLayout>
      <PublicInfoPageHeader
        pageTestId="categories-learn-more-page"
        eyebrow={t("categories-learn-more-page-eyebrow", "Learn more")}
        title={t(
          "categories-learn-more-page-title",
          "Categories and subcategories"
        )}
        intro={t(
          "categories-learn-more-page-intro",
          "Learn how parent and child categories organize your catalog, and see a realistic paint-shop example you can copy for your own business."
        )}
      />

      <Stack sx={publicInfoSectionStackSx}>
        <PublicInfoCallout testId="categories-learn-more-page-callout">
          {t(
            "categories-learn-more-page-callout",
            "Every product must belong to a category. Build your tree from broad groups down to the specific shelf where customers find an item."
          )}
        </PublicInfoCallout>

        <Box sx={publicInfoSectionPanelSx}>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t(
              "categories-learn-more-parent-child-heading",
              "Parent and child categories"
            )}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mb: 1.5 }}>
            {t(
              "categories-learn-more-parent-child-body-1",
              "A parent category is the larger group. Child categories sit underneath it and narrow the group step by step. You can build several levels — for example Paint → Water-Based Paint → Interior Water-Based Paint → Wall Paint."
            )}
          </Typography>
          <Typography variant="body1" sx={publicInfoBodySx}>
            {t(
              "categories-learn-more-parent-child-body-2",
              "When you add a category, choose a parent if this group should live inside another one. Pick None (top-level) for your main headings, such as Paint, Tools, or Beverages."
            )}
          </Typography>
        </Box>

        <Box>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t(
              "categories-learn-more-tree-heading",
              "Example: paint shop category tree"
            )}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mb: 2 }}>
            {t(
              "categories-learn-more-tree-intro",
              "A paint retailer might structure categories like this. Leaf categories (the last level) are where products are usually assigned."
            )}
          </Typography>
          <CategoryMermaidDiagram
            chart={PAINT_CATEGORY_STRUCTURE_MERMAID}
            testId="categories-learn-more-paint-tree"
            titleKey="categories-learn-more-tree-title"
            titleFallback="Paint category hierarchy"
          />
        </Box>

        <Box sx={publicInfoSectionPanelSx}>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t(
              "categories-learn-more-logic-heading",
              "Better category logic for Kivunova FMS"
            )}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mb: 1.5 }}>
            {t(
              "categories-learn-more-logic-body-1",
              "Use nested categories for navigation and grouping. Capture product details such as finish, color, and pack size on the product form — not as extra category levels unless they help your team browse."
            )}
          </Typography>
          <Box component="ul" sx={{ ...publicInfoBodySx, pl: 2.5, mb: 1.5 }}>
            <Box component="li" sx={{ mb: 0.75 }}>
              {t("categories-learn-more-logic-li-1", "Category: Paint")}
            </Box>
            <Box component="li" sx={{ mb: 0.75 }}>
              {t(
                "categories-learn-more-logic-li-2",
                "Subcategory 1: Water-Based Paint"
              )}
            </Box>
            <Box component="li" sx={{ mb: 0.75 }}>
              {t(
                "categories-learn-more-logic-li-3",
                "Subcategory 2: Interior Paint"
              )}
            </Box>
            <Box component="li">
              {t(
                "categories-learn-more-logic-li-4",
                "Subcategory 3: Wall Paint"
              )}
            </Box>
          </Box>
          <Typography variant="body1" sx={publicInfoBodySx}>
            {t(
              "categories-learn-more-logic-body-2",
              "Product attributes on the form can include finish type, color, packaging size, brand, coverage area, and base unit — you do not need a separate category for each finish or color."
            )}
          </Typography>
        </Box>

        <Box>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t(
              "categories-learn-more-form-heading",
              "How this maps to your product form"
            )}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mb: 2 }}>
            {t(
              "categories-learn-more-form-intro",
              "For a paint shop, combine category levels with product fields like this:"
            )}
          </Typography>
          <TableContainer
            sx={{
              borderRadius: 2,
              border: (theme) =>
                `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
              overflowX: "auto"
            }}
            data-testid="categories-learn-more-form-table"
          >
            <Table size="small" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08)
                  }}
                >
                  <TableCell sx={{ fontWeight: 700, width: "38%" }}>
                    {t("categories-learn-more-form-table-field-col", "Field")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t(
                      "categories-learn-more-form-table-example-col",
                      "Example values"
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {CATEGORY_PRODUCT_FORM_ROWS.map((row) => (
                  <TableRow
                    key={row.id}
                    data-testid={`categories-learn-more-form-row-${row.id}`}
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      "&:nth-of-type(even)": {
                        bgcolor: (theme) =>
                          alpha(theme.palette.primary.main, 0.03)
                      }
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        verticalAlign: "top"
                      }}
                    >
                      {t(row.fieldKey, row.fieldFallback)}
                    </TableCell>
                    <TableCell
                      sx={{ ...publicInfoBodySx, verticalAlign: "top" }}
                    >
                      {t(row.exampleKey, row.exampleFallback)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={publicInfoSectionPanelSx}>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t("categories-learn-more-example-heading", "Example product path")}
          </Typography>
          <Typography variant="body1" sx={{ ...publicInfoBodySx, mb: 2 }}>
            {t(
              "categories-learn-more-example-intro",
              "One sellable product might sit at the deepest category level:"
            )}
          </Typography>
          <CategoryMermaidDiagram
            chart={EXAMPLE_PRODUCT_PATH_MERMAID}
            testId="categories-learn-more-example-tree"
          />
        </Box>

        <Box sx={publicInfoSectionPanelSx}>
          <Typography component="h2" sx={publicInfoSectionTitleSx}>
            {t("categories-learn-more-setup-heading", "Typical setup flow")}
          </Typography>
          <Box component="ol" sx={publicInfoOrderedListSx}>
            <Box component="li">
              {t(
                "categories-learn-more-setup-step-1",
                "Create top-level categories (Paint, Tools, Accessories)."
              )}
            </Box>
            <Box component="li">
              {t(
                "categories-learn-more-setup-step-2",
                "Add child categories under each parent until you reach the level where products belong."
              )}
            </Box>
            <Box component="li">
              {t(
                "categories-learn-more-setup-step-3",
                "Add products and assign each one to the most specific (leaf) category."
              )}
            </Box>
            <Box component="li">
              {t(
                "categories-learn-more-setup-step-4",
                "Use product fields for finish, color, size, and other attributes."
              )}
            </Box>
          </Box>
        </Box>

        <PublicInfoCtaPanel
          title={t(
            "categories-learn-more-cta-title",
            "Ready to build your category tree?"
          )}
          testId="categories-learn-more-cta"
        >
          <Typography variant="body1" sx={publicInfoBodySx}>
            {t(
              "categories-learn-more-cta-body",
              "Follow the step-by-step run book to add categories, then add products."
            )}{" "}
            <MuiLink
              component={Link}
              to={addCategorySopPath}
              underline="hover"
              sx={publicInfoLinkSx}
            >
              {t(
                "categories-learn-more-cta-add-category-link",
                "How to add a category"
              )}
            </MuiLink>
            {t("categories-learn-more-cta-mid", " · ")}{" "}
            <MuiLink
              component={Link}
              to={addProductSopPath}
              underline="hover"
              sx={publicInfoLinkSx}
            >
              {t(
                "categories-learn-more-cta-add-product-link",
                "How to add a product"
              )}
            </MuiLink>
            {t("categories-learn-more-cta-mid", " · ")}{" "}
            <MuiLink
              component={Link}
              to={sopsHubPath}
              underline="hover"
              sx={publicInfoLinkSx}
            >
              {t("categories-learn-more-cta-run-books-link", "Run books")}
            </MuiLink>
            {t("categories-learn-more-cta-mid", " · ")}{" "}
            <MuiLink
              component={Link}
              to={userGuidePath}
              underline="hover"
              sx={publicInfoLinkSx}
            >
              {t("categories-learn-more-cta-guide-link", "User guide")}
            </MuiLink>
            {t("categories-learn-more-cta-mid", " · ")}{" "}
            <MuiLink
              component={Link}
              to={contactPath}
              underline="hover"
              sx={publicInfoLinkSx}
            >
              {t("categories-learn-more-cta-contact-link", "Contact us")}
            </MuiLink>
          </Typography>
        </PublicInfoCtaPanel>
      </Stack>
    </PublicMarketingLayout>
  );
};

export default CategoriesLearnMorePage;
